import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import styled from 'styled-components';

const CallbackContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  background-color: #0a0e17;
  color: #ffffff;
`;

const LoadingText = styled.h2`
  font-size: 20px;
  color: #00ff4c;
  text-align: center;
`;

const ErrorMessage = styled.div`
  background-color: #f44336;
  color: white;
  padding: 20px;
  border-radius: 5px;
  margin-top: 20px;
  text-align: center;
`;

const Button = styled.button`
  margin-top: 20px;
  padding: 10px 20px;
  background-color: #5865F2;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 16px;
  font-weight: bold;
  
  &:hover {
    background-color: #4752c4;
  }
`;

const HelpText = styled.div`
  background-color: rgba(33, 150, 243, 0.1);
  border-left: 3px solid #2196f3;
  padding: 15px;
  margin-top: 20px;
  text-align: left;
  font-size: 14px;
  color: #bbd8ff;
  max-width: 500px;
`;

const AuthCallback = () => {
  const [error, setError] = useState('');
  const [errorDetails, setErrorDetails] = useState('');
  const { handleCallback } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  useEffect(() => {
    const processAuth = async () => {
      try {
        // URL'den parametreleri al
        const queryParams = new URLSearchParams(location.search);
        const code = queryParams.get('code');
        const state = queryParams.get('state');
        const error = queryParams.get('error');
        
        // Discord'dan error dönerse
        if (error) {
          console.error('Discord auth error:', error);
          setError('Discord yetkilendirme hatası: ' + error);
          setErrorDetails('Discord yetkilendirme işlemi reddedildi veya iptal edildi.');
          return;
        }
        
        if (!code) {
          setError('Yetkilendirme kodu bulunamadı.');
          setErrorDetails('URL\'de kod parametresi eksik.');
          return;
        }
        
        // State kontrolü
        const savedState = localStorage.getItem('discord_oauth_state');
        if (state !== savedState) {
          console.error('State mismatch', { received: state, saved: savedState });
          setError('Güvenlik doğrulaması başarısız.');
          setErrorDetails('State değeri eşleşmiyor. Bu bir CSRF saldırısı olabilir.');
          return;
        }
        
        // State'i temizle
        localStorage.removeItem('discord_oauth_state');
        
        console.log('Authorization code received:', code.substring(0, 5) + '...');
        
        // Auth işlemini tamamla
        const success = await handleCallback(code);
        
        if (success) {
          // Başarılıysa admin paneline yönlendir
          navigate('/admin');
        } else {
          setError('Yetkilendirme işlemi sırasında bir hata oluştu.');
          setErrorDetails('Token alınamadı veya kullanıcı bilgileri getirilemedi.');
        }
      } catch (err) {
        console.error('Auth işlemi hatası:', err);
        setError('Kimlik doğrulama sırasında bir hata oluştu.');
        setErrorDetails(err.message || 'Bilinmeyen hata');
      }
    };
    
    processAuth();
  }, [location, handleCallback, navigate]);
  
  const retryLogin = () => {
    navigate('/auth/login');
  };
  
  // Özel hata mesajları için yardımcı fonksiyon
  const getErrorHelp = (errorMessage) => {
    if (errorMessage.includes('Discord token request failed: 400')) {
      return (
        <HelpText>
          <strong>400 Hatası Çözümü:</strong>
          <ul>
            <li>Discord Developer Portal'daki Client ID ve Client Secret değerlerinin doğru olduğundan emin olun.</li>
            <li>Discord Developer Portal'da "OAuth2 → Redirects" bölümündeki yönlendirme URL'inin tam olarak <code>http://localhost:3000/auth/callback</code> şeklinde olduğunu kontrol edin.</li>
            <li>Yönlendirme URL'inde yazım hatası olmadığından emin olun, URL'in sonunda slash (/) olmamalıdır.</li>
            <li>Discord uygulamanız için "bot" tipinin değil "OAuth2" yapılandırmasının doğru yapıldığından emin olun.</li>
          </ul>
        </HelpText>
      );
    }
    return null;
  };
  
  return (
    <CallbackContainer>
      {error ? (
        <>
          <LoadingText>Yetkilendirme Hatası</LoadingText>
          <ErrorMessage>{error}</ErrorMessage>
          {errorDetails && <ErrorMessage>{errorDetails}</ErrorMessage>}
          {getErrorHelp(error)}
          <Button onClick={retryLogin}>Tekrar Dene</Button>
        </>
      ) : (
        <LoadingText>Discord hesabınızla giriş yapılıyor...</LoadingText>
      )}
    </CallbackContainer>
  );
};

export default AuthCallback; 